// filepath: /Users/samuelshen/Github/Piano/src/components/Piano.tsx
import React, { useState, useEffect, useMemo } from 'react';
import Key from './Key';
import Controls from './Controls';
import Recorder from './Recorder';
import { isBlackKey, playNote, stopNote, keyboardMap } from '../utils/audioUtils';
import './Piano.css';

// Define the note event interface
interface NoteEvent {
  note: string;
  startTime: number;
  duration?: number;
}

const Piano: React.FC = () => {
  // Define the min and max octave ranges
  const MIN_OCTAVE = 1;
  const MAX_OCTAVE = 7;
  
  // Set the starting octave range
  const [octaveRange, setOctaveRange] = useState<number[]>([3, 4, 5]);
  const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set());
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedNotes, setRecordedNotes] = useState<NoteEvent[]>([]);
  const [recordingStartTime, setRecordingStartTime] = useState(0);
  
  // Generate notes based on current octave range
  const notes = useMemo(() => {
    const allNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const notesArray: string[] = [];
    
    octaveRange.forEach(octave => {
      allNotes.forEach(note => {
        notesArray.push(`${note}${octave}`);
      });
    });
    
    return notesArray;
  }, [octaveRange]);
  
  // Recording handlers
  const startRecording = () => {
    setIsRecording(true);
    setRecordedNotes([]);
    setRecordingStartTime(Date.now());
  };
  
  const stopRecording = () => {
    setIsRecording(false);
  };

  // Handle mouse interactions with recording support
  const handleMouseDown = (note: string) => {
    playNote(note);
    setPressedKeys(prev => new Set([...prev, note]));
    
    // Record the note if recording is active
    if (isRecording) {
      const currentTime = Date.now();
      const startTime = currentTime - recordingStartTime;
      
      setRecordedNotes(prev => [
        ...prev,
        { note, startTime }
      ]);
    }
  };

  const handleMouseUp = (note: string) => {
    stopNote(note);
    setPressedKeys(prev => {
      const newSet = new Set([...prev]);
      newSet.delete(note);
      return newSet;
    });
    
    // Update note duration if recording
    if (isRecording) {
      const currentTime = Date.now();
      const releaseTime = currentTime - recordingStartTime;
      
      setRecordedNotes(prev => {
        // Find the last instance of this note and update its duration
        const newNotes = [...prev];
        for (let i = newNotes.length - 1; i >= 0; i--) {
          if (newNotes[i].note === note && !newNotes[i].duration) {
            newNotes[i] = {
              ...newNotes[i],
              duration: releaseTime - newNotes[i].startTime
            };
            break;
          }
        }
        return newNotes;
      });
    }
  };

  // Modified keyboard mapping based on current octave
  const currentKeyboardMap = useMemo(() => {
    // Adjust the keyboardMap based on octave range
    const octaveOffset = octaveRange[0] - 3; // Default mapping is for octave 3
    const newMapping: Record<string, string> = {};
    
    Object.entries(keyboardMap).forEach(([key, note]) => {
      const noteName = note.slice(0, -1); // Get note without octave
      const noteOctave = parseInt(note.slice(-1)); // Get octave number
      const newOctave = noteOctave + octaveOffset;
      
      // Only include notes that are within our current octave range
      if (newOctave >= octaveRange[0] && newOctave <= octaveRange[2]) {
        newMapping[key] = `${noteName}${newOctave}`;
      }
    });
    
    return newMapping;
  }, [octaveRange]);
  
  // Handle keyboard interactions
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return; // Prevent repeat events when key is held down
      const note = currentKeyboardMap[e.key.toLowerCase()];
      if (note && !pressedKeys.has(note)) {
        playNote(note);
        setPressedKeys(prev => new Set([...prev, note]));
        
        // Record the note if recording is active
        if (isRecording) {
          const currentTime = Date.now();
          const startTime = currentTime - recordingStartTime;
          
          setRecordedNotes(prev => [
            ...prev,
            { note, startTime }
          ]);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const note = currentKeyboardMap[e.key.toLowerCase()];
      if (note) {
        stopNote(note);
        setPressedKeys(prev => {
          const newSet = new Set([...prev]);
          newSet.delete(note);
          return newSet;
        });
        
        // Update note duration if recording
        if (isRecording) {
          const currentTime = Date.now();
          const releaseTime = currentTime - recordingStartTime;
          
          setRecordedNotes(prev => {
            // Find the last instance of this note and update its duration
            const newNotes = [...prev];
            for (let i = newNotes.length - 1; i >= 0; i--) {
              if (newNotes[i].note === note && !newNotes[i].duration) {
                newNotes[i] = {
                  ...newNotes[i],
                  duration: releaseTime - newNotes[i].startTime
                };
                break;
              }
            }
            return newNotes;
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pressedKeys, currentKeyboardMap, isRecording, recordingStartTime]);
  
  // Octave range handlers
  const changeOctaveRange = (direction: 'up' | 'down') => {
    if (direction === 'up' && octaveRange[2] < MAX_OCTAVE) {
      setOctaveRange(prev => prev.map(octave => octave + 1));
    } else if (direction === 'down' && octaveRange[0] > MIN_OCTAVE) {
      setOctaveRange(prev => prev.map(octave => octave - 1));
    }
  };
  
  const canMoveDown = octaveRange[0] > MIN_OCTAVE;
  const canMoveUp = octaveRange[2] < MAX_OCTAVE;

  // Function to calculate correct position for a black key
  const getBlackKeyPosition = (note: string): number => {
    const noteName = note.slice(0, -1);
    const octave = parseInt(note.slice(-1));
    
    // Find octave index in our current range
    const octaveIndex = octaveRange.indexOf(octave);
    if (octaveIndex === -1) return 0;
    
    // Calculate position in pixels
    const whiteKeyWidth = 60; // Width of one white key
    
    // Get the index of the white keys in this octave (0-6 for C through B)
    const whiteKeyIndices: Record<string, number> = {
      'C': 0, 'D': 1, 'E': 2, 'F': 3, 'G': 4, 'A': 5, 'B': 6
    };
    
    // Calculate position based on the adjacent white keys
    // Each black key is positioned precisely between its adjacent white keys
    const blackKeyOffset = 0.08 * whiteKeyWidth; // Slightly increased offset to shift keys a bit left
    
    // Calculate the absolute position based on the octave start and white key positions
    switch (noteName) {
      case 'C#': // Between C and D
        const cKeyPosition = octaveIndex * 7 * whiteKeyWidth + whiteKeyIndices['C'] * whiteKeyWidth;
        return cKeyPosition + whiteKeyWidth - blackKeyOffset; 
        
      case 'D#': // Between D and E
        const dKeyPosition = octaveIndex * 7 * whiteKeyWidth + whiteKeyIndices['D'] * whiteKeyWidth;
        return dKeyPosition + whiteKeyWidth - blackKeyOffset;
        
      case 'F#': // Between F and G
        const fKeyPosition = octaveIndex * 7 * whiteKeyWidth + whiteKeyIndices['F'] * whiteKeyWidth;
        return fKeyPosition + whiteKeyWidth - blackKeyOffset;
        
      case 'G#': // Between G and A
        const gKeyPosition = octaveIndex * 7 * whiteKeyWidth + whiteKeyIndices['G'] * whiteKeyWidth;
        return gKeyPosition + whiteKeyWidth - blackKeyOffset;
        
      case 'A#': // Between A and B
        const aKeyPosition = octaveIndex * 7 * whiteKeyWidth + whiteKeyIndices['A'] * whiteKeyWidth;
        return aKeyPosition + whiteKeyWidth - blackKeyOffset;
        
      default:
        return 0; // Default fallback
    }
  };

  // Separate white and black keys for layering
  const whiteKeys = notes.filter(note => !isBlackKey(note));
  const blackKeys = notes.filter(note => isBlackKey(note));

  return (
    <div className="piano-container">
      <div className="keyboard-info">
   
        <p className="keyboard-mapping">Z-M: Octave 2 | A-J: Octave 3 | K-\: Octave 4 | 1-=: Octaves 5-6</p>
        <p className="octave-display">Current Octave Range: {octaveRange[0]}-{octaveRange[2]}</p>
      </div>
      
      <Controls 
        onChangeOctaveRange={changeOctaveRange} 
        canMoveDown={canMoveDown} 
        canMoveUp={canMoveUp} 
      />
      
      <Recorder
        isRecording={isRecording}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
        recordedNotes={recordedNotes}
      />
      
      <div className="piano">
        <div className="white-keys">
          {whiteKeys.map(note => {
            // Find keyboard key for this note
            const keyboardKey = Object.entries(currentKeyboardMap).find(
              ([_, mappedNote]) => mappedNote === note
            )?.[0];
            
            return (
              <Key
                key={note}
                note={note}
                keyType="white"
                isPressed={pressedKeys.has(note)}
                keyboardKey={keyboardKey?.toUpperCase()}
                onMouseDown={() => handleMouseDown(note)}
                onMouseUp={() => handleMouseUp(note)}
                onMouseLeave={() => pressedKeys.has(note) && handleMouseUp(note)}
              />
            );
          })}
        </div>
        <div className="black-keys">
          {blackKeys.map(note => {
            // Find keyboard key for this note
            const keyboardKey = Object.entries(currentKeyboardMap).find(
              ([_, mappedNote]) => mappedNote === note
            )?.[0];
            
            // Calculate position
            const position = getBlackKeyPosition(note);
            
            return (
              <div 
                key={note} 
                style={{ 
                  position: 'absolute',
                  left: `${position}px`,
                  transform: 'translateX(-35%)', // Adjusted to move keys slightly back to the left
                  zIndex: 2,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Key
                  note={note}
                  keyType="black"
                  isPressed={pressedKeys.has(note)}
                  keyboardKey={keyboardKey?.toUpperCase()}
                  onMouseDown={() => handleMouseDown(note)}
                  onMouseUp={() => handleMouseUp(note)}
                  onMouseLeave={() => pressedKeys.has(note) && handleMouseUp(note)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Piano;
